import React, { useState, useEffect, useRef, useCallback } from 'react';
import { t } from '../utils/i18n';

/**
 * WebcamCaptureModal
 * High quality webcam capture modal tailored for Military Personnel Registration and ID Card generation.
 * Features:
 * - Device selection (multiple webcams / USB cameras)
 * - Live viewfinder with military ID photo framing overlay (oval head & shoulder guide, 3:4 aspect ratio)
 * - Horizontal mirror toggle
 * - Optional 3-second countdown timer for steady posing
 * - Precise 3:4 portrait cropping to 600x800 px (ideal for ID card printing)
 * - Review & Retake workflow
 * - Audio shutter feedback via Web Audio API
 * - Clean hardware track release on close / unmount
 */
const WebcamCaptureModal = ({
  isOpen,
  onClose,
  onCapture,
  title = 'Capture Personnel Photo'
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMirrored, setIsMirrored] = useState(true);
  const [useCountdown, setUseCountdown] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [flashActive, setFlashActive] = useState(false);
  const [copiedFlag, setCopiedFlag] = useState(false);
  const [copiedOrigin, setCopiedOrigin] = useState(false);

  // Play synthesized shutter sound
  const playShutterSound = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioCtx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(120, ctx.currentTime + 0.08);

      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.09);
    } catch {
      // Audio playback is optional
    }
  }, []);

  // Stop active video stream
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => track.stop());
      } catch (err) {
        console.warn('Error stopping webcam track:', err);
      }
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Enumerate video devices
  const updateDeviceList = useCallback(async () => {
    if (!navigator?.mediaDevices?.enumerateDevices) return;
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = allDevices.filter((d) => d.kind === 'videoinput');
      const formatted = videoInputs.map((d, idx) => ({
        deviceId: d.deviceId,
        label: d.label || `Camera ${idx + 1} (${d.deviceId.slice(0, 8)}...)`
      }));
      setDevices(formatted);
    } catch (err) {
      console.warn('Failed to enumerate media devices:', err);
    }
  }, []);

  // Start webcam stream with legacy fallback
  const startStream = useCallback(async (deviceIdToUse) => {
    const getUserMediaFn =
      navigator?.mediaDevices?.getUserMedia
        ? (constraints) => navigator.mediaDevices.getUserMedia(constraints)
        : (navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia)
        ? (constraints) =>
            new Promise((res, rej) => {
              const legacyFn =
                navigator.getUserMedia ||
                navigator.webkitGetUserMedia ||
                navigator.mozGetUserMedia ||
                navigator.msGetUserMedia;
              legacyFn.call(navigator, constraints, res, rej);
            })
        : null;

    if (!getUserMediaFn) {
      const isInsecureHttp =
        window.location.protocol === 'http:' &&
        window.location.hostname !== 'localhost' &&
        window.location.hostname !== '127.0.0.1';

      if (isInsecureHttp) {
        setError(
          `Browser security blocks live webcam streaming on unencrypted HTTP (${window.location.host}). You can take a photo directly using the "Take Photo with Camera" button below, or enable the browser flag.`
        );
      } else {
        setError(
          'Webcam access is not supported in this browser or environment. Please use HTTPS or localhost with a modern browser.'
        );
      }
      return;
    }

    setIsLoading(true);
    setError('');
    stopStream();

    const constraints = {
      audio: false,
      video: deviceIdToUse
        ? {
            deviceId: { exact: deviceIdToUse },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        : {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
    };

    try {
      const stream = await getUserMediaFn(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }

      setIsLoading(false);
      // Re-read devices now that permission is granted (labels will be populated)
      await updateDeviceList();
    } catch (err) {
      console.error('getUserMedia error:', err);
      setIsLoading(false);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError(
          'Camera access was denied. Please allow camera permissions in your browser address bar and click Retry.'
        );
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError(
          'No camera was found. Please ensure your webcam is plugged in and recognized by your system.'
        );
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError(
          'Camera is currently in use by another application or tab. Please close other camera apps and retry.'
        );
      } else {
        setError(`Failed to connect to webcam: ${err.message || 'Unknown error'}`);
      }
    }
  }, [stopStream, updateDeviceList]);

  // Initialize stream when modal opens
  useEffect(() => {
    if (isOpen) {
      setCapturedImage(null);
      setCountdown(null);
      setError('');
      startStream(selectedDeviceId);
    } else {
      stopStream();
      setCapturedImage(null);
      setCountdown(null);
    }

    return () => {
      stopStream();
    };
  }, [isOpen, selectedDeviceId, startStream, stopStream]);

  // Handle device change
  const handleDeviceChange = (e) => {
    const newId = e.target.value;
    setSelectedDeviceId(newId);
  };

  // Handle file upload or native camera snapshot fallback
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 600;
        canvas.height = 800;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        const iw = img.width;
        const ih = img.height;
        const targetAspect = 3 / 4;
        let cropWidth, cropHeight;
        if (iw / ih > targetAspect) {
          cropHeight = ih;
          cropWidth = cropHeight * targetAspect;
        } else {
          cropWidth = iw;
          cropHeight = cropWidth / targetAspect;
        }
        const cropX = (iw - cropWidth) / 2;
        const cropY = (ih - cropHeight) / 2;
        ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, 600, 800);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        setCapturedImage(dataUrl);
        setError("");
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    // Reset inputs
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Perform the actual snap from the video element and crop to 3:4 portrait ID card ratio
  const performSnap = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setError('Camera video feed is not ready. Please try again.');
      return;
    }

    playShutterSound();
    setFlashActive(true);
    setTimeout(() => setFlashActive(false), 200);

    const vw = video.videoWidth;
    const vh = video.videoHeight;

    // 3:4 portrait aspect ratio (width:height = 0.75)
    const targetAspect = 3 / 4;
    let cropWidth;
    let cropHeight;

    if (vw / vh > targetAspect) {
      // Video is wider than 3:4 (e.g. 16:9 or 4:3 landscape)
      cropHeight = vh * 0.94;
      cropWidth = cropHeight * targetAspect;
    } else {
      // Video is taller than 3:4
      cropWidth = vw * 0.94;
      cropHeight = cropWidth / targetAspect;
    }

    const cropX = Math.max(0, (vw - cropWidth) / 2);
    const cropY = Math.max(0, (vh - cropHeight) / 2);

    // High quality standard ID card resolution (600x800 px)
    const outWidth = 600;
    const outHeight = 800;

    const canvas = document.createElement('canvas');
    canvas.width = outWidth;
    canvas.height = outHeight;
    const ctx = canvas.getContext('2d');

    // Smooth scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    if (isMirrored) {
      ctx.translate(outWidth, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(
      video,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      outWidth,
      outHeight
    );

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setCapturedImage(dataUrl);
    setCountdown(null);
  }, [isMirrored, playShutterSound]);

  // Trigger snapshot (instant or with 3-second countdown)
  const handleSnapClick = () => {
    if (useCountdown) {
      setCountdown(3);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev === null) {
            clearInterval(timer);
            return null;
          }
          if (prev <= 1) {
            clearInterval(timer);
            performSnap();
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      performSnap();
    }
  };

  // Retake photo
  const handleRetake = () => {
    setCapturedImage(null);
    setCountdown(null);
    if (!streamRef.current) {
      startStream(selectedDeviceId);
    }
  };

  // Confirm photo
  const handleConfirm = () => {
    if (capturedImage && onCapture) {
      onCapture(capturedImage);
      stopStream();
      onClose();
    }
  };

  // Close modal and cleanup
  const handleClose = () => {
    stopStream();
    onClose();
  };

  const currentOrigin =
    typeof window !== 'undefined'
      ? window.location.origin || `${window.location.protocol}//${window.location.host}`
      : 'http://102.128.130.75';

  const isInsecureHttp =
    typeof window !== 'undefined' &&
    window.location.protocol === 'http:' &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1';

  if (!isOpen) return null;

  return (
    <div
      className="webcam-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="webcam-modal-title"
    >
      <div className="webcam-modal-card">
        {/* Modal Header */}
        <div className="webcam-modal-header">
          <div className="webcam-modal-header-left">
            <span className="webcam-badge-icon">📷</span>
            <div>
              <h3 id="webcam-modal-title" className="webcam-modal-title">
                {title || t('capturePersonnelPhoto', 'Capture Personnel Photo')}
              </h3>
              <p className="webcam-modal-subtitle">
                Official Military ID Card Standard · 3:4 Portrait Framing
              </p>
            </div>
          </div>
          <button
            type="button"
            className="webcam-modal-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="webcam-modal-body">
          {/* Top Controls Bar */}
          {!capturedImage && (
            <div className="webcam-controls-bar">
              {devices.length > 0 && (
                <div className="webcam-device-select-wrap">
                  <label htmlFor="webcam-device-select" className="webcam-label">
                    Camera:
                  </label>
                  <select
                    id="webcam-device-select"
                    value={selectedDeviceId}
                    onChange={handleDeviceChange}
                    className="webcam-select"
                  >
                    <option value="">Default Camera</option>
                    {devices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="webcam-options-wrap">
                <button
                  type="button"
                  className={`webcam-tool-btn ${isMirrored ? 'active' : ''}`}
                  onClick={() => setIsMirrored((m) => !m)}
                  title="Toggle horizontal mirror"
                >
                  <span>↔️</span>
                  <span>{isMirrored ? 'Mirrored' : 'Normal'}</span>
                </button>

                <button
                  type="button"
                  className={`webcam-tool-btn ${useCountdown ? 'active' : ''}`}
                  onClick={() => setUseCountdown((c) => !c)}
                  title="Toggle 3-second timer"
                >
                  <span>⏱️</span>
                  <span>{useCountdown ? '3s Timer ON' : 'Timer OFF'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Viewfinder Container */}
          <div className="webcam-viewfinder-container">
            {/* Shutter flash animation */}
            {flashActive && <div className="webcam-flash-overlay" />}

            {/* Error / Insecure State */}
            {error && (
              <div className="webcam-error-box">
                <div className="webcam-error-icon">⚠️</div>
                <h4 className="webcam-error-title">Webcam Stream Blocked</h4>
                <p className="webcam-error-desc">{error}</p>

                {/* Instant Actions */}
                <div style={{ display: "flex", gap: "10px", marginTop: "16px", flexWrap: "wrap", justifyContent: "center" }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => cameraInputRef.current?.click()}
                    style={{ minWidth: "180px", fontWeight: "600", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
                  >
                    <span>📸</span>
                    <span>Take Photo with Camera</span>
                  </button>

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => fileInputRef.current?.click()}
                    style={{ minWidth: "140px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
                  >
                    <span>📁</span>
                    <span>Upload Photo File</span>
                  </button>

                  <button
                    type="button"
                    className="btn inv"
                    onClick={() => startStream(selectedDeviceId)}
                    style={{ minWidth: "120px" }}
                  >
                    🔄 Retry Stream
                  </button>

                  {/* Hidden inputs for direct capture */}
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                </div>

                {/* Chrome / Edge Flag Instructions for Live Streaming on Insecure Origins */}
                {isInsecureHttp && (
                  <div style={{ marginTop: "16px", padding: "12px 16px", background: "rgba(255,255,255,0.06)", borderRadius: "8px", fontSize: "12px", textAlign: "left", lineHeight: "1.6", color: "#cbd5e1", maxWidth: "520px", border: "1px solid rgba(251,191,36,0.3)" }}>
                    <div style={{ fontWeight: "700", color: "#fbbf24", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>💡</span>
                      <span>To enable Live Video Streaming on this IP ({window.location.host}):</span>
                    </div>
                    <div>
                      <strong>Step 1:</strong> Open a new tab in Chrome/Edge and go to:
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "3px", marginBottom: "6px" }}>
                        <code style={{ color: "#93c5fd", background: "rgba(0,0,0,0.4)", padding: "3px 6px", borderRadius: "4px", fontSize: "11px", wordBreak: "break-all" }}>
                          chrome://flags/#unsafely-treat-insecure-origin-as-secure
                        </code>
                        <button
                          type="button"
                          className="btn inv"
                          style={{ padding: "2px 8px", fontSize: "11px", height: "auto", minHeight: "24px" }}
                          onClick={() => {
                            navigator.clipboard?.writeText('chrome://flags/#unsafely-treat-insecure-origin-as-secure');
                            setCopiedFlag(true);
                            setTimeout(() => setCopiedFlag(false), 2000);
                          }}
                        >
                          {copiedFlag ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    <div>
                      <strong>Step 2:</strong> In the text box, paste your current origin:
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "3px", marginBottom: "6px" }}>
                        <code style={{ color: "#93c5fd", background: "rgba(0,0,0,0.4)", padding: "3px 6px", borderRadius: "4px", fontSize: "11px" }}>
                          {currentOrigin}
                        </code>
                        <button
                          type="button"
                          className="btn inv"
                          style={{ padding: "2px 8px", fontSize: "11px", height: "auto", minHeight: "24px" }}
                          onClick={() => {
                            navigator.clipboard?.writeText(currentOrigin);
                            setCopiedOrigin(true);
                            setTimeout(() => setCopiedOrigin(false), 2000);
                          }}
                        >
                          {copiedOrigin ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    <div><strong>Step 3:</strong> Select <strong>Enabled</strong> and click <strong>Relaunch</strong> at the bottom.</div>
                  </div>
                )}
              </div>
            )}

            {/* Loading State */}
            {isLoading && !error && (
              <div className="webcam-loading-box">
                <div className="webcam-spinner" />
                <p>Initializing camera feed...</p>
              </div>
            )}

            {/* Live Video Feed */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`webcam-video-element ${isMirrored ? 'mirrored' : ''} ${
                capturedImage || error ? 'd-none' : ''
              }`}
            />

            {/* Live ID Framing Guide Overlay */}
            {!capturedImage && !error && !isLoading && (
              <div className="webcam-overlay-guide">
                {/* 3:4 ID Portrait Bounding Frame */}
                <div className="webcam-guide-portrait-box">
                  {/* Oval head guide */}
                  <div className="webcam-guide-oval">
                    {/* Eye line */}
                    <div className="webcam-guide-eyeline" />
                  </div>
                  {/* Shoulder curve guide */}
                  <div className="webcam-guide-shoulders" />
                  {/* Crosshairs */}
                  <div className="webcam-guide-crosshair" />

                  {/* Corner marks */}
                  <span className="corner-bracket top-left" />
                  <span className="corner-bracket top-right" />
                  <span className="corner-bracket bottom-left" />
                  <span className="corner-bracket bottom-right" />
                </div>

                <div className="webcam-guide-instructions">
                  Align face inside the oval and shoulders with the guide
                </div>
              </div>
            )}

            {/* Live Countdown Display */}
            {countdown !== null && (
              <div className="webcam-countdown-overlay">
                <span className="webcam-countdown-number">{countdown}</span>
              </div>
            )}

            {/* Captured Photo Preview in Review Mode */}
            {capturedImage && (
              <div className="webcam-preview-box">
                <img
                  src={capturedImage}
                  alt="Captured Personnel"
                  className="webcam-preview-img"
                />
                <div className="webcam-preview-badge">
                  ✓ Photo Captured · 600×800 px (3:4 Portrait)
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer / Actions */}
        <div className="webcam-modal-footer">
          {!capturedImage ? (
            <>
              <button
                type="button"
                className="btn inv"
                onClick={handleClose}
                disabled={countdown !== null}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn webcam-capture-action-btn"
                onClick={handleSnapClick}
                disabled={isLoading || !!error || countdown !== null}
              >
                📸 {useCountdown ? 'Start 3s Timer' : 'Capture Photo'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn inv webcam-retake-btn"
                onClick={handleRetake}
              >
                🔄 Retake Photo
              </button>

              <button
                type="button"
                className="btn webcam-confirm-btn"
                onClick={handleConfirm}
              >
                ✅ Accept & Use Photo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebcamCaptureModal;
